import { createFrontendClient } from '@pipedream/sdk/browser';
import { ConnectedAccount } from '../../context/UserContext';
import { BASE_URL } from '../constants';
import { EPipedreamConnectErrorType } from '../../types/pipedream';
import { apiFetch } from '../ApiFetch';
import { throwIfHttpError } from '../httpErrorMessage';
import { getUserSessionCookie } from '../../utils/SessionCookie';

let client: ReturnType<typeof createFrontendClient> | null = null;
let currentOrganizationId: string | null = null;

const getOAuthClientId = async (app: string): Promise<string> => {
  const sessionCookie = getUserSessionCookie();
  const res = await apiFetch(`${BASE_URL}/connector/oauth_client_id/${app}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    }
  });

  await throwIfHttpError(
    res,
    `Failed to fetch oauth client id (${res.status})`
  );

  const data = await res.json();
  return data.oauth_client_id;
};

export const connectToIntegration = async (
  app: string,
  organizationId: string
): Promise<ConnectedAccount> => {
  const sessionCookie = getUserSessionCookie();
  // Recreate client if organizationId changed or doesn't exist
  if (!client || currentOrganizationId !== organizationId) {
    client = createFrontendClient({
      externalUserId: organizationId,
      tokenCallback: async () => {
        const res = await apiFetch(`${BASE_URL}/connector/connect_token`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
          }
        });

        await throwIfHttpError(
          res,
          `Failed to fetch connect token (${res.status})`
        );

        const data = await res.json();
        return {
          token: data.token,
          connectLinkUrl: data.connect_link_url,
          expiresAt: data.expires_at
        };
      }
    });
    currentOrganizationId = organizationId;
  }

  // Fetch oauth client id for the app
  const oauthAppId = await getOAuthClientId(app);

  return new Promise((resolve, reject) => {
    try {
      if (!client) {
        reject(new Error('Pipedream client not initialized'));
        return;
      }

      client.connectAccount({
        app,
        oauthAppId,
        onSuccess: (account: any) => {
          resolve({ app: account.app, connectionId: account.connectionId });
        },
        onError: (err) => {
          const errorMessage = err?.message || err || 'Unknown error';
          reject(
            new Error(
              `${EPipedreamConnectErrorType.CONNECTION_FAILED}:${errorMessage}`
            )
          );
        },
        onClose: () => {
          reject(new Error(EPipedreamConnectErrorType.USER_CANCELLED));
        }
      });
    } catch (err: any) {
      // Handle synchronous errors from connectAccount
      reject(
        new Error(
          `${EPipedreamConnectErrorType.CONNECTION_FAILED}:${err?.message || err || 'Failed to initialize connection'}`
        )
      );
    }
  });
};

export const fetchConnectedAccounts = async (): Promise<ConnectedAccount[]> => {
  const sessionCookie = getUserSessionCookie();
  const res = await apiFetch(`${BASE_URL}/connector/connections`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    }
  });

  await throwIfHttpError(
    res,
    `Failed to fetch connections (${res.status})`
  );

  const data = await res.json();
  return data.map((conn: any) => ({
    app: conn.app,
    connectionId: conn.connectionId
  }));
};

export const disconnectIntegration = async (
  connectionId: string
): Promise<void> => {
  const sessionCookie = getUserSessionCookie();
  const res = await apiFetch(
    `${BASE_URL}/connector/disconnect?connection_id=${connectionId}`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      }
    }
  );

  await throwIfHttpError(res, `Failed to disconnect (${res.status})`);
  // Invalidate cached Pipedream client so the next connect uses a fresh connect_token.
  client = null;
  currentOrganizationId = null;
};

export { client };
